import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, extractApiError } from "@/lib/api";
import { calculateAgeFromBirthDate } from "@/lib/age";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import ClientDialog from "@/components/ClientDialog";
import Pill from "@/components/Pill";
import ConfirmDialog from "@/components/ConfirmDialog";
import ResponsiveDataView from "@/components/responsive/ResponsiveDataView";
import ResponsivePageHeader from "@/components/responsive/ResponsivePageHeader";
import { formatDateTime, useLanguage, useTranslation } from "@/i18n/LanguageContext";
import { ChevronRight, Plus, SlidersHorizontal, Trash2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import usePaginatedResource from "@/hooks/usePaginatedResource";

const SEARCH_DEBOUNCE_MS = 250;
const ASSIGN_PAGE_LIMIT = 25;

function getInitials(name) {
  if (!name) return "?";
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("") || "?";
}

export default function Sensors() {
  const t = useTranslation();
  const { language } = useLanguage();
  const { page, loading, reload, changePage, updateItems } = usePaginatedResource(
    async (params, { signal }) => (await api.get("/sensors", { params, signal })).data,
    { onError: (err) => toast.error(extractApiError(err, t("sensors.toast.failedLoad"))) },
  );
  const [filter, setFilter] = useState("all");
  const [assigning, setAssigning] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Admin readiness is derived from the nested live join — no `profileComplete` on the wire.
  // `s.client` is null both when nothing is assigned and when the assigned client was deleted (orphaned).
  const isReady = (s) => s.client != null;

  const filtered = page.items.filter((s) => {
    if (filter === "complete") return isReady(s);
    if (filter === "incomplete") return !isReady(s);
    return true;
  });

  const needsProfile = useMemo(
    () => page.items.filter((s) => !isReady(s)),
    [page.items],
  );

  const replaceSensor = (next) => {
    updateItems((items) => items.map((x) => (x.id === next.id ? next : x)));
  };

  const onAssigned = (sensor) => {
    replaceSensor(sensor);
  };

  const onDelete = async (sensor) => {
    try {
      await api.delete(`/sensors/${encodeURIComponent(sensor.sensorId)}`);
      setConfirmDelete(null);
      reload();
      toast.success(t("sensors.toast.deleted"));
    } catch (err) {
      toast.error(extractApiError(err));
    }
  };

  const filters = (
    <div className="flex flex-wrap items-center gap-2">
      <SlidersHorizontal className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.5} />
      {["all", "complete", "incomplete"].map((f) => (
        <button
          key={f}
          type="button"
          onClick={() => setFilter(f)}
          data-testid={`sensors-filter-${f}`}
          className={`h-8 rounded-lg border px-3 font-display text-[11px] font-semibold uppercase tracking-wider transition-colors ${
            filter === f
              ? "border-input bg-secondary text-foreground"
              : "border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground"
          }`}
        >
          {t(`sensors.filters.${f}`)}
        </button>
      ))}
    </div>
  );

  const sensorState = (s) => (
    isReady(s)
      ? <Pill variant="complete" testId={`sensor-state-${s.sensorId}`}>{t("common.states.complete")}</Pill>
      : <Pill variant="incomplete" testId={`sensor-state-${s.sensorId}`}>{t("common.states.incomplete")}</Pill>
  );

  return (
    <div data-testid="sensors-page" className="space-y-6">
      <ResponsivePageHeader
        title={t("sensors.title")}
        description={t("sensors.description")}
        filters={filters}
      />

      {needsProfile.length > 0 && (
        <section
          className="rounded-xl border border-border bg-card p-4"
          data-testid="sensors-needs-profile"
        >
          <div className="mb-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="inline-block h-2 w-2 shrink-0 self-center rounded-full bg-amber-400" aria-hidden />
            <h2 className="font-display text-lg tracking-tight">
              {t("sensors.needsProfile.title")}
            </h2>
            <span className="text-xs text-muted-foreground">
              {t("sensors.needsProfile.subtitle", { count: needsProfile.length })}
            </span>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {needsProfile.map((s) => (
              <div
                key={s.id}
                className="flex flex-col gap-3 rounded-lg border border-border bg-background p-3"
                data-testid={`needs-profile-card-${s.sensorId}`}
              >
                <div className="font-mono text-sm">{s.sensorId}</div>
                <Button
                  className="w-full rounded-lg"
                  onClick={() => setAssigning(s)}
                  data-testid={`needs-profile-assign-${s.sensorId}`}
                >
                  {t("sensors.needsProfile.assign")}
                </Button>
              </div>
            ))}
          </div>
        </section>
      )}

      <ResponsiveDataView
        items={filtered}
        loading={loading}
        emptyState={page.total === 0 ? t("sensors.empty") : t("sensors.noMatches")}
        getRowKey={(s) => s.id}
        getRowTestId={(s) => `sensor-row-${s.sensorId}`}
        tableTestId="sensors-table"
        loadingTestId="sensors-loading"
        emptyTestId="sensors-empty"
        columns={[
          { key: "sensorId", header: t("sensors.columns.sensorId"), cell: (s) => <span className="font-mono text-xs">{s.sensorId}</span> },
          { key: "name", header: t("sensors.columns.name"), cell: (s) => s.client?.name || <span className="text-muted-foreground">-</span> },
          { key: "age", header: t("sensors.columns.age"), cell: (s) => <span className="font-mono text-xs">{s.client?.age ?? <span className="text-muted-foreground">-</span>}</span> },
          { key: "state", header: t("sensors.columns.state"), cell: sensorState },
          { key: "created", header: t("sensors.columns.created"), cell: (s) => <span className="font-mono text-[11px] text-muted-foreground">{formatDateTime(s.createdAt, language)}</span> },
        ]}
        mobileTitle={(s) => s.client?.name || s.sensorId}
        mobileSubtitle={(s) => s.sensorId}
        mobileBadge={sensorState}
        mobileFields={[
          { label: t("common.fields.name"), value: (s) => s.client?.name || "-" },
          { label: t("common.fields.age"), value: (s) => s.client?.age ?? "-" },
          { label: t("common.fields.created"), value: (s) => formatDateTime(s.createdAt, language), priority: "secondary" },
        ]}
        rowActions={[
          { key: "reassign", label: t("sensors.actions.reassign"), icon: UserPlus, onClick: setAssigning, testId: (s) => `reassign-sensor-${s.sensorId}` },
          { key: "delete", label: t("common.actions.delete"), icon: Trash2, destructive: true, onClick: setConfirmDelete, testId: (s) => `delete-sensor-${s.sensorId}` },
        ]}
        pagination={{
          total: page.total,
          limit: page.limit,
          offset: page.offset,
          onChange: changePage,
          testId: "sensors-pager",
        }}
      />

      <SensorAssignDialog
        sensor={assigning}
        onClose={() => setAssigning(null)}
        onAssigned={onAssigned}
      />
      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        title={t("sensors.delete.title")}
        description={t("sensors.delete.description", { sensorId: confirmDelete?.sensorId })}
        confirmLabel={t("common.actions.delete")}
        destructive
        onConfirm={() => onDelete(confirmDelete)}
        testId="sensor-delete-dialog"
      />
    </div>
  );
}

function SensorAssignDialog({ sensor, onClose, onAssigned }) {
  const t = useTranslation();
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [assigningId, setAssigningId] = useState(null);
  const [creating, setCreating] = useState(false);
  const sentinelRef = useRef(null);
  const requestIdRef = useRef(0);

  // Reset state when dialog opens for a new sensor
  useEffect(() => {
    if (!sensor) return;
    setQ("");
    setDebouncedQ("");
    setClients([]);
    setTotal(0);
    setCreating(false);
  }, [sensor]);

  // Debounce q
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [q]);

  // Load first page on dialog-open or whenever debounced query changes
  useEffect(() => {
    if (!sensor) return;
    const requestId = ++requestIdRef.current;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const params = { limit: ASSIGN_PAGE_LIMIT, offset: 0 };
        if (debouncedQ) params.q = debouncedQ;
        const { data } = await api.get("/clients", { params });
        if (cancelled || requestId !== requestIdRef.current) return;
        setClients(data.items);
        setTotal(data.total);
      } catch (err) {
        if (cancelled || requestId !== requestIdRef.current) return;
        setClients([]);
        setTotal(0);
        toast.error(extractApiError(err, t("sensors.assign.loadError")));
      } finally {
        if (!cancelled && requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sensor, debouncedQ, t]);

  // Infinite scroll
  useEffect(() => {
    if (!sensor) return;
    const node = sentinelRef.current;
    if (!node) return;
    const hasMore = clients.length < total;
    if (!hasMore) return;
    const observer = new IntersectionObserver(async (entries) => {
      const entry = entries[0];
      if (!entry?.isIntersecting || loading || loadingMore) return;
      const requestId = ++requestIdRef.current;
      setLoadingMore(true);
      try {
        const params = { limit: ASSIGN_PAGE_LIMIT, offset: clients.length };
        if (debouncedQ) params.q = debouncedQ;
        const { data } = await api.get("/clients", { params });
        if (requestId !== requestIdRef.current) return;
        setClients((prev) => [...prev, ...data.items]);
        setTotal(data.total);
      } catch (err) {
        toast.error(extractApiError(err, t("sensors.assign.loadError")));
      } finally {
        setLoadingMore(false);
      }
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [sensor, clients, total, loading, loadingMore, debouncedQ, t]);

  const patchClientId = async (clientId, successKey) => {
    if (!sensor || assigningId) return;
    setAssigningId(clientId ?? "__clear__");
    try {
      const { data } = await api.patch(
        `/sensors/${encodeURIComponent(sensor.sensorId)}`,
        { clientId },
      );
      onAssigned(data);
      toast.success(t(successKey));
      onClose();
    } catch (err) {
      toast.error(extractApiError(err));
    } finally {
      setAssigningId(null);
    }
  };

  const assignToClient = (client) =>
    patchClientId(client.id, "sensors.toast.assigned");

  const clearAssignment = () =>
    patchClientId(null, "sensors.toast.cleared");

  const createAndAssign = async (values) => {
    if (!sensor) return;
    try {
      const { data: newClient } = await api.post("/clients", values);
      const { data: updatedSensor } = await api.patch(
        `/sensors/${encodeURIComponent(sensor.sensorId)}`,
        { clientId: newClient.id },
      );
      setCreating(false);
      onAssigned(updatedSensor);
      toast.success(t("sensors.toast.createdAndAssigned"));
      onClose();
    } catch (err) {
      toast.error(extractApiError(err));
    }
  };

  if (!sensor) return null;

  const shownWithoutStrap = clients.filter(
    (c) => !c.assignedSensorIds || c.assignedSensorIds.length === 0,
  ).length;
  const hasMore = clients.length < total;

  return (
    <>
      <Dialog open={!!sensor && !creating} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="border-border sm:max-w-lg" data-testid="sensor-assign-dialog">
          <DialogHeader>
            <div className="mono-eyebrow text-[10px]">{t("sensors.assign.eyebrow")}</div>
            <DialogTitle className="font-display text-2xl tracking-tight font-mono">
              {sensor.sensorId}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {t("sensors.assign.title")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("sensors.assign.searchPlaceholder")}
              data-testid="sensor-assign-search"
            />

            <div
              className="max-h-80 overflow-y-auto overflow-x-hidden rounded-lg border border-border"
              data-testid="sensor-assign-list"
            >
              {loading && clients.length === 0 ? (
                <div className="px-3 py-6 text-center text-xs text-muted-foreground" data-testid="sensor-assign-loading">
                  {t("sensors.assign.loading")}
                </div>
              ) : clients.length === 0 ? (
                <div className="px-3 py-6 text-center text-xs text-muted-foreground" data-testid="sensor-assign-empty">
                  {debouncedQ ? t("sensors.assign.empty") : t("sensors.assign.noClients")}
                </div>
              ) : (
                <ul>
                  {clients.map((c) => {
                    const sensorIds = c.assignedSensorIds ?? [];
                    const firstSensor = sensorIds[0];
                    const extra = sensorIds.length > 1 ? sensorIds.length - 1 : 0;
                    const age = calculateAgeFromBirthDate(c.birthDate);
                    return (
                      <li key={c.id} className="border-b border-border last:border-b-0">
                        <button
                          type="button"
                          onClick={() => assignToClient(c)}
                          disabled={!!assigningId}
                          className="flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-muted disabled:opacity-60"
                          data-testid={`sensor-assign-client-${c.id}`}
                        >
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium text-muted-foreground">
                            {getInitials(c.name)}
                          </span>
                          <span className="flex min-w-0 flex-1 flex-col">
                            <span className="truncate text-sm font-medium">{c.name}</span>
                            <span className="truncate text-xs text-muted-foreground">{c.email}</span>
                          </span>
                          <span className="flex shrink-0 items-center gap-2 text-xs">
                            {firstSensor ? (
                              <span className="flex items-center gap-1 font-mono text-primary">
                                {firstSensor}
                                {extra > 0 && (
                                  <span className="text-muted-foreground">+{extra}</span>
                                )}
                              </span>
                            ) : age != null ? (
                              <span className="font-mono text-muted-foreground">{age}y</span>
                            ) : null}
                            <ChevronRight className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                          </span>
                        </button>
                      </li>
                    );
                  })}
                  {hasMore && (
                    <li
                      ref={sentinelRef}
                      className="px-3 py-3 text-center text-[11px] text-muted-foreground"
                      data-testid="sensor-assign-sentinel"
                    >
                      {loadingMore ? t("sensors.assign.loadingMore") : "·"}
                    </li>
                  )}
                </ul>
              )}
            </div>

            <div className="flex flex-col gap-2 pt-1 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
              <span className="text-xs text-muted-foreground" data-testid="sensor-assign-without-strap">
                {t("sensors.assign.withoutStrap", { count: shownWithoutStrap })}
              </span>
              <div className="flex flex-col gap-2 sm:flex-row sm:gap-2">
                {sensor.clientId && (
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full rounded-lg text-destructive hover:text-destructive sm:w-auto"
                    onClick={clearAssignment}
                    disabled={!!assigningId}
                    data-testid="sensor-assign-clear"
                  >
                    {t("sensors.assign.clear")}
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  className="w-full rounded-lg sm:w-auto"
                  onClick={() => setCreating(true)}
                  data-testid="sensor-assign-create-new"
                >
                  <Plus className="mr-1 h-4 w-4" />
                  {t("sensors.assign.createNew")}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {creating && (
        <ClientDialog
          client={{}}
          onClose={() => setCreating(false)}
          onSave={createAndAssign}
        />
      )}
    </>
  );
}
